// The photo picker: the photo itself opens the file chooser, with a camera
// badge saying so. Shared by /account and the /welcome first-run flow, so the
// two cannot drift.
//
// `done` receives the updated user after an upload or a removal; both callers
// re-render from it.

import { removeAvatar, uploadAvatar } from '../api/client.js';
import { el } from '../dom.js';
import { icon } from '../icons.js';
import type { CurrentUser } from '@msc/shared';

export function avatarPicker(user: CurrentUser, done: (u: CurrentUser) => void): HTMLElement {
  const error = el('p', { class: 'form-error', hidden: true });
  const busy = el('p', { class: 'muted small', hidden: true }, 'Uploading…');

  const preview = user.avatarThumbUrl
    ? el('img', { class: 'avatar-preview', src: user.avatarThumbUrl, alt: 'Your photo' })
    : el('div', { class: 'avatar-preview avatar-empty', 'aria-hidden': 'true' }, initials(user));

  // The PHOTO is the control: a <label> around it opens the file picker, with a
  // camera badge saying so. The input stays in the DOM, visually hidden rather
  // than `display: none`, so it is still focusable and the label still opens it
  // on iOS. That also retires the file input's engine-specific intrinsic width,
  // which once dragged the whole account grid past the viewport.
  //
  // `accept` is a HINT to the file picker and nothing more -- the server sniffs
  // the real bytes and refuses anything that is not one of its four formats, so
  // this cannot be the check.
  const fileInput = el('input', {
    id: 'ac-avatar',
    type: 'file',
    class: 'visually-hidden',
    accept: 'image/jpeg,image/png,image/webp,image/gif',
    'aria-label': user.avatarThumbUrl ? 'Change photo' : 'Add a photo',
  }) as HTMLInputElement;

  const removeBtn = el('button', { type: 'button', class: 'btn btn-link' }, 'Remove photo');

  async function run(work: () => Promise<CurrentUser>): Promise<void> {
    error.hidden = true;
    busy.hidden = false;
    fileInput.disabled = true;
    removeBtn.setAttribute('disabled', 'disabled');
    try {
      done(await work());
    } catch (err) {
      error.textContent =
        err instanceof Error ? err.message : 'Something went wrong. Try again.';
      error.hidden = false;
      busy.hidden = true;
      fileInput.disabled = false;
      removeBtn.removeAttribute('disabled');
      // Clear it, or picking the SAME file again fires no `change` event and the
      // retry looks like a dead control.
      fileInput.value = '';
    }
  }

  // Uploads on pick rather than behind a Save button. There is one field and one
  // outcome, so a second click to confirm it is a step with nothing in it.
  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    void run(() => uploadAvatar(file));
  });

  removeBtn.addEventListener('click', () => {
    void run(() => removeAvatar());
  });

  return el('div', { class: 'avatar-picker-wrap' }, [
    el('div', { class: 'avatar-row' }, [
      el('label', { for: 'ac-avatar', class: 'avatar-picker' }, [
        preview,
        el('span', { class: 'avatar-badge', 'aria-hidden': 'true' }, [icon('camera')]),
      ]),
      fileInput,
      el('div', { class: 'avatar-controls' }, [
        el(
          'p',
          { class: 'muted small' },
          user.avatarThumbUrl ? 'Tap your photo to change it.' : 'Tap the circle to add a photo.',
        ),
        ...(user.avatarThumbUrl ? [removeBtn] : []),
      ]),
    ]),
    busy,
    error,
  ]);
}

/** Initials for the empty state, matching what the feed already does for posts. */
function initials(user: CurrentUser): string {
  const first = (user.firstName || '').trim().charAt(0);
  const last = (user.lastName || '').trim().charAt(0);
  const both = `${first}${last}`.toUpperCase();
  return both || (user.email || '?').charAt(0).toUpperCase();
}
